import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import {
  signInWithGoogle,
  signOut,
  clearError as clearAuthError,
} from '../store/slices/authSlice';

// Custom hook for authentication
export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);

  const signIn = useCallback(() => {
    dispatch(signInWithGoogle());
  }, [dispatch]);

  const signOutUser = useCallback(() => {
    dispatch(signOut());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  return {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    retryCount: auth.retryCount,
    isRetrying: auth.isRetrying,
    authMetrics: auth.authMetrics,
    signIn,
    signOut: signOutUser,
    clearError,
  };
};

// Custom hook for UI state
export const useUI = () => {
  const dispatch = useDispatch();
  const ui = useSelector(state => state.ui);

  const setShowNavbar = useCallback(
    show => {
      dispatch({ type: 'ui/setShowNavbar', payload: show });
    },
    [dispatch]
  );

  const toggleMenu = useCallback(() => {
    dispatch({ type: 'ui/toggleMenu' });
  }, [dispatch]);

  const closeMenu = useCallback(() => {
    dispatch({ type: 'ui/closeMenu' });
  }, [dispatch]);

  const toggleProfileMenu = useCallback(() => {
    dispatch({ type: 'ui/toggleProfileMenu' });
  }, [dispatch]);

  const openProfileMenu = useCallback(() => {
    dispatch({ type: 'ui/openProfileMenu' });
  }, [dispatch]);

  const closeProfileMenu = useCallback(() => {
    dispatch({ type: 'ui/closeProfileMenu' });
  }, [dispatch]);

  const setLoading = useCallback(
    (isLoading, message = '') => {
      dispatch({ type: 'ui/setLoading', payload: { isLoading, message } });
    },
    [dispatch]
  );

  const clearLoading = useCallback(() => {
    dispatch({ type: 'ui/clearLoading' });
  }, [dispatch]);

  const setError = useCallback(
    (message, type = 'general') => {
      dispatch({ type: 'ui/setError', payload: { message, type } });
    },
    [dispatch]
  );

  const clearError = useCallback(() => {
    dispatch({ type: 'ui/clearError' });
  }, [dispatch]);

  const addNotification = useCallback(
    notification => {
      dispatch({ type: 'ui/addNotification', payload: notification });
    },
    [dispatch]
  );

  const removeNotification = useCallback(
    id => {
      dispatch({ type: 'ui/removeNotification', payload: id });
    },
    [dispatch]
  );

  return {
    ...ui,
    setShowNavbar,
    toggleMenu,
    closeMenu,
    toggleProfileMenu,
    openProfileMenu,
    closeProfileMenu,
    setLoading,
    clearLoading,
    setError,
    clearError,
    addNotification,
    removeNotification,
  };
};

// Custom hook for app state
export const useApp = () => {
  const dispatch = useDispatch();
  const app = useSelector(state => state.app);

  const setVerified = useCallback(
    verified => {
      dispatch({ type: 'app/setVerified', payload: verified });
    },
    [dispatch]
  );

  const setHasShownSplash = useCallback(
    hasShown => {
      dispatch({ type: 'app/setHasShownSplash', payload: hasShown });
    },
    [dispatch]
  );

  const setCurrentPage = useCallback(
    page => {
      dispatch({ type: 'app/setCurrentPage', payload: page });
    },
    [dispatch]
  );

  const updatePreferences = useCallback(
    preferences => {
      dispatch({ type: 'app/updatePreferences', payload: preferences });
    },
    [dispatch]
  );

  const trackPageView = useCallback(
    pageName => {
      dispatch({ type: 'app/trackPageView', payload: pageName });
    },
    [dispatch]
  );

  const addError = useCallback(
    error => {
      dispatch({ type: 'app/addError', payload: error });
    },
    [dispatch]
  );

  const clearErrors = useCallback(() => {
    dispatch({ type: 'app/clearErrors' });
  }, [dispatch]);

  return {
    ...app,
    setVerified,
    setHasShownSplash,
    setCurrentPage,
    updatePreferences,
    trackPageView,
    addError,
    clearErrors,
  };
};

// Custom hook for combined state management
export const useAppState = () => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const ui = useUI();
  const app = useApp();

  // Combined actions
  const handleVerificationChange = useCallback(
    (verified, isShowingSplashOrAgeVerification = false) => {
      app.setVerified(verified);
      const shouldShowNavbar = verified && !isShowingSplashOrAgeVerification;
      ui.setShowNavbar(shouldShowNavbar);
    },
    [app, ui]
  );

  const initializeApp = useCallback(async () => {
    // Check if user is already authenticated when page loads
    try {
      // Import checkAuthState dynamically to avoid early execution issues
      const { checkAuthState } = await import('../store/slices/authSlice');
      await dispatch(checkAuthState()).unwrap();
    } catch (error) {
      // Continue even if auth check fails
    }
  }, [dispatch]);

  const handleError = useCallback(
    (error, type = 'general') => {
      ui.setError(error.message || error, type);
      app.addError({
        type,
        message: error.message || error,
        stack: error.stack,
      });
    },
    [ui, app]
  );

  return {
    auth,
    ui,
    app,
    handleVerificationChange,
    initializeApp,
    handleError,
  };
};

// Custom hook for responsive design
export const useResponsive = () => {
  const dispatch = useDispatch();
  const { isMobile, screenSize } = useSelector(state => state.ui);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobileDevice = width < 768;
      const newScreenSize =
        width < 480 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';

      dispatch({ type: 'ui/setIsMobile', payload: isMobileDevice });
      dispatch({ type: 'ui/setScreenSize', payload: newScreenSize });
    };

    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  return { isMobile, screenSize };
};

// Custom hook for accessibility
export const useAccessibility = () => {
  const dispatch = useDispatch();
  const accessibility = useSelector(state => state.ui.accessibility);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = e => {
      dispatch({
        type: 'ui/updateAccessibility',
        payload: { reducedMotion: e.matches },
      });
    };

    handleChange(mediaQuery); // Initial call
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [dispatch]);

  useEffect(() => {
    // Check for high contrast preference
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    const handleChange = e => {
      dispatch({
        type: 'ui/updateAccessibility',
        payload: { highContrast: e.matches },
      });
    };

    handleChange(mediaQuery); // Initial call
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [dispatch]);

  return accessibility;
};
